import { useMemo } from 'react';
import { useActivePlatform } from './use-active-platform';
import { hasPermission, PERMISSION_ACTIONS } from 'src/utils/permissions';
import type {
  Permission,
  ResourcePath,
  AllowedActionsForPath,
  ResourcePathAllowing,
} from 'src/utils/permissions';
import { IS_LOCALHOST } from 'src/config-global';

export const usePermissions = () => {
  const { userRole, permissionMatrix, features } = useActivePlatform();
  const { isResourceAvailable } = features;

  const checkPermission = useMemo(
    () =>
      <P extends ResourcePath>(path: P, action: AllowedActionsForPath<P>): boolean =>
        isResourceAvailable(path) && hasPermission(permissionMatrix, path, action as Permission),
    [isResourceAvailable, permissionMatrix]
  );

  const canGet = useMemo(
    () =>
      (path: ResourcePathAllowing<'get'>): boolean =>
        isResourceAvailable(path) &&
        (IS_LOCALHOST || hasPermission(permissionMatrix, path, PERMISSION_ACTIONS.GET)),
    [isResourceAvailable, permissionMatrix]
  );

  const canWrite = useMemo(
    () =>
      (path: ResourcePathAllowing<'write'>): boolean =>
        isResourceAvailable(path) &&
        (IS_LOCALHOST || hasPermission(permissionMatrix, path, PERMISSION_ACTIONS.WRITE)),
    [isResourceAvailable, permissionMatrix]
  );

  const canDelete = useMemo(
    () =>
      (path: ResourcePathAllowing<'delete'>): boolean =>
        isResourceAvailable(path) &&
        (IS_LOCALHOST || hasPermission(permissionMatrix, path, PERMISSION_ACTIONS.DELETE)),
    [isResourceAvailable, permissionMatrix]
  );

  return {
    checkPermission,
    canGet,
    canWrite,
    canDelete,
    userRole,
  };
};

export { RESOURCES } from 'src/utils/permissions';
